/**
 * The client end of the deal protocol's transport: one `sync` call, over whichever wire is up.
 *
 * The protocol itself is the server's (`server/src/deal.ts`): a `sync` carries the moves since the last one
 * (`fromIndex`, `moves`) and asks for the card at position `n`; the answer is a `hand` ({ cards, count, view })
 * or an `error` ({ code }). This file only moves those messages.
 *
 * ## One socket per round, and an HTTP twin behind it
 *
 * A round opens a WebSocket to `/api/games/ws` (the API's own origin, cookie auth: no token is ever in a
 * message). A call goes over the socket when it is open. When it is not (still connecting, refused, dropped), or
 * when it goes unanswered for `DEAL_TIMEOUT_MS`, the call is made as `POST /api/games/:id/deal` with the same
 * body instead, and the socket is reopened in the background with backoff. The caller sees none of this: `sync`
 * is one attempt that answers with a hand, a refusal the server gave, or "lost".
 *
 * ## What this file does not do
 *
 * It does not retry. A lost attempt is the caller's to resend, with the same `fromIndex` (the server skips what
 * it has already applied, so a resend is harmless): the caller owns the moves, this owns the wire.
 */

import type { CardId } from '@/game/types';
import { DEAL_TIMEOUT_MS, postDeal, wsUrl, type SessionView, type Submission } from '@/net/api';

/** A play, or an undo. `tMs` is ms since the round started on this client; the server ignores it. */
export type Move =
  | { readonly pile: number; readonly tMs: number }
  | { readonly undo: true; readonly tMs: number };

export interface SyncRequest {
  /** Which instance of the client is calling: a random id per engine. Not an identity; the cookie is. */
  readonly driver: string;
  /** Take the round over from whoever is driving it: sent until the server has answered this engine once. */
  readonly claim?: boolean;
  /** The number of moves the server has been told of: `moves[0]` is move `fromIndex`. */
  readonly fromIndex: number;
  /** Which card to deal (the n-th of the round, from 0). */
  readonly n: number;
  readonly moves: readonly Move[];
}

export interface HandReply {
  readonly n: number;
  readonly cards: readonly CardId[];
  /** The server's applied-move count after this call. */
  readonly count: number;
  readonly view: SessionView;
  /** Only on the reply that settled the round. */
  readonly result?: Submission;
}

/**
 * What one attempt came to. `lost` is anything that is not an answer: a timeout, a dropped socket, a network
 * failure, a 5xx, a rate limit. The server never saw the call, or its answer never got here; resend it.
 */
export type DealOutcome =
  | { readonly kind: 'hand'; readonly reply: HandReply }
  /** `result` only on `game-over`: the settlement the server kept, for a caller that never saw the reply that settled the round. */
  | { readonly kind: 'refused'; readonly code: string; readonly result?: Submission }
  | { readonly kind: 'lost' };

/** Waits between attempts: 250, 500, 1000, 2000 ms, then 2000 for as long as it takes. */
export const BACKOFF_MS = [250, 500, 1000, 2000] as const;

/** The wait before attempt number `failures + 1`, after `failures` lost ones in a row (0 for the first resend). */
export function backoffMs(failures: number): number {
  return BACKOFF_MS[Math.min(Math.max(0, failures), BACKOFF_MS.length - 1)]!;
}

/**
 * The server's refusals that mean "try again", not "no": the call may well succeed later.
 */
const RETRYABLE = new Set(['rate-limited', 'internal']);

/** Every code the protocol has (`DEAL_STATUS` on the server). Anything else in an error is not the protocol's. */
const PROTOCOL_CODES = new Set([
  'bad-request',
  'bad-hand',
  'bad-id',
  'no-such-hand',
  'bad-move',
  'forbidden',
  'game-not-found',
  'game-not-playing',
  'game-over',
  'hand-too-early',
  'move-gap',
  'move-conflict',
  'taken-over',
  'rate-limited',
  'too-large',
  'unauthorized',
  'internal',
]);

/** Close codes after which reopening the socket would only fail the same way (or fight another tab for it). */
const NO_REOPEN = new Set([4401, 4403, 4429, 4409]);

function isHand(m: unknown): m is HandReply {
  const h = m as Partial<HandReply> | null;
  return (
    typeof h === 'object' &&
    h !== null &&
    typeof h.count === 'number' &&
    Array.isArray(h.cards) &&
    typeof h.view === 'object' &&
    h.view !== null
  );
}

/** Sort an error code into a refusal the caller must act on, or a loss it should just resend through. */
function refusal(code: unknown, result?: unknown): DealOutcome {
  if (typeof code !== 'string' || !PROTOCOL_CODES.has(code) || RETRYABLE.has(code)) return { kind: 'lost' };
  return typeof result === 'object' && result !== null
    ? { kind: 'refused', code, result: result as Submission }
    : { kind: 'refused', code };
}

/** The server closes the socket of a driver that another tab or device of the same player replaced: close code 4409. */
export const CLOSE_TAKEN_OVER = 4409;

/** A random id for one client instance (`crypto.randomUUID` needs a secure context, and the game is also played over a LAN). */
export function newDriverId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface DealLinkOptions {
  /** Another tab or device took the round over (the socket was told, or closed 4409): this one is done. */
  readonly onTakenOver?: () => void;
  /** One attempt's patience. */
  readonly timeoutMs?: number;
  /** Do not open a socket at all (HTTP only). */
  readonly socket?: boolean;
}

export class DealLink {
  readonly #gameId: string;
  readonly #timeoutMs: number;
  readonly #useSocket: boolean;
  #opened = false;
  readonly #onTakenOver: (() => void) | undefined;
  #socket: WebSocket | null = null;
  #ready = false;
  #closed = false;
  #giveUp = false;
  /** Consecutive socket failures since a socket last answered: the backoff's input. */
  #failures = 0;
  #reopen: ReturnType<typeof setTimeout> | null = null;
  #rid = 0;
  readonly #inflight = new Map<string, (outcome: DealOutcome) => void>();

  constructor(gameId: string, options: DealLinkOptions = {}) {
    this.#gameId = gameId;
    this.#timeoutMs = options.timeoutMs ?? DEAL_TIMEOUT_MS;
    this.#onTakenOver = options.onTakenOver;
    this.#useSocket = options.socket ?? typeof globalThis.WebSocket === 'function';
  }

  /**
   * Open the socket. Not in the constructor: an engine that is made and never used (a lookup that found a round the
   * player had since moved on from, one thrown away by another tab's claim) must not hold one of the account's
   * few sockets. Called when the round starts and, failing that, by the first call.
   */
  open(): void {
    if (this.#opened || this.#closed) return;
    this.#opened = true;
    if (this.#useSocket) this.#connect();
  }

  /** The wire the next call would use. */
  get transport(): 'ws' | 'http' {
    return this.#ready ? 'ws' : 'http';
  }

  /** One attempt, over the socket when it is up and over HTTP when it is not. Never throws. */
  sync(request: SyncRequest, timeoutMs?: number): Promise<DealOutcome> {
    if (this.#closed) return Promise.resolve({ kind: 'lost' });
    this.open();
    const body = {
      t: 'sync',
      rid: String(++this.#rid),
      gameId: this.#gameId,
      driver: request.driver,
      ...(request.claim ? { claim: true } : {}),
      fromIndex: request.fromIndex,
      n: request.n,
      moves: request.moves,
    };
    const socket = this.#socket;
    const patience = timeoutMs ?? this.#timeoutMs;
    return this.#ready && socket ? this.#viaSocket(socket, body, patience) : this.#viaHttp(body, patience);
  }

  /** Stop: close the socket, forget the retry timer, and let any attempt in flight come back lost. */
  close(): void {
    this.#closed = true;
    if (this.#reopen !== null) clearTimeout(this.#reopen);
    this.#reopen = null;
    const socket = this.#socket;
    this.#socket = null;
    this.#ready = false;
    try {
      socket?.close();
    } catch {
      /* already closed */
    }
    this.#loseInflight();
  }

  /* --------------------------------------------------------------- wire -- */

  #viaSocket(socket: WebSocket, body: { rid: string }, patience: number): Promise<DealOutcome> {
    return new Promise((resolve) => {
      const rid = body.rid;
      const timer = setTimeout(() => {
        // Unanswered on a socket that says it is open: it is not usable. Take the whole socket down (the
        // reopen is the background retry) and let the caller's resend go out over HTTP.
        this.#inflight.delete(rid);
        this.#drop(socket);
        resolve({ kind: 'lost' });
      }, patience);
      this.#inflight.set(rid, (outcome) => {
        clearTimeout(timer);
        this.#inflight.delete(rid);
        resolve(outcome);
      });
      try {
        socket.send(JSON.stringify(body));
      } catch {
        clearTimeout(timer);
        this.#inflight.delete(rid);
        this.#drop(socket);
        resolve({ kind: 'lost' });
      }
    });
  }

  async #viaHttp(body: object, patience: number): Promise<DealOutcome> {
    const answer = await postDeal(this.#gameId, body, patience);
    if (answer.ok) return isHand(answer.value) ? { kind: 'hand', reply: answer.value } : { kind: 'lost' };
    const detail = answer.body as { result?: unknown } | null | undefined;
    return refusal(answer.error, detail?.result);
  }

  /* ------------------------------------------------------------- socket -- */

  #connect(): void {
    if (this.#closed || this.#giveUp) return;
    let socket: WebSocket;
    try {
      socket = new WebSocket(wsUrl());
    } catch {
      this.#scheduleReopen();
      return;
    }
    this.#socket = socket;
    socket.onopen = () => {
      if (this.#socket === socket) this.#ready = true;
    };
    socket.onmessage = (event: MessageEvent) => {
      if (this.#socket === socket) this.#onMessage(event.data);
    };
    socket.onclose = (event: CloseEvent) => {
      if (NO_REOPEN.has(event.code)) this.#giveUp = true;
      this.#drop(socket);
      if (event.code === CLOSE_TAKEN_OVER && !this.#closed) this.#onTakenOver?.();
    };
    socket.onerror = () => this.#drop(socket);
  }

  #onMessage(data: unknown): void {
    let message: { t?: unknown; rid?: unknown; code?: unknown } & Partial<HandReply>;
    try {
      message = JSON.parse(String(data));
    } catch {
      return;
    }
    if (typeof message !== 'object' || message === null) return;
    const done = typeof message.rid === 'string' ? this.#inflight.get(message.rid) : undefined;
    if (message.t === 'hand') {
      // A socket that answers is a socket that works: the next drop starts the backoff afresh.
      this.#failures = 0;
      done?.(isHand(message) ? { kind: 'hand', reply: message } : { kind: 'lost' });
    } else if (message.t === 'error') {
      if (done) done(refusal(message.code, (message as { result?: unknown }).result));
      // Pushed, not an answer: the server telling this driver another has the round.
      else if (message.code === 'taken-over' && !this.#closed) this.#onTakenOver?.();
    }
  }

  /** The socket is gone (or must be treated as gone): fail what was waiting on it and try again later. */
  #drop(socket: WebSocket): void {
    if (this.#socket !== socket) return;
    this.#socket = null;
    this.#ready = false;
    try {
      socket.close();
    } catch {
      /* already closed */
    }
    this.#loseInflight();
    this.#scheduleReopen();
  }

  #loseInflight(): void {
    for (const done of [...this.#inflight.values()]) done({ kind: 'lost' });
  }

  #scheduleReopen(): void {
    if (this.#closed || this.#giveUp || this.#reopen !== null) return;
    const wait = backoffMs(this.#failures++);
    this.#reopen = setTimeout(() => {
      this.#reopen = null;
      this.#connect();
    }, wait);
  }
}
